/**
 * Mud domain write layer + full-job read — injected onto the shared Oxbo-P
 * client (`pApi`) so the contract files (`src/api/pApi.ts`, `src/api/types.ts`)
 * stay untouched and branches merge cleanly. All request/response types are
 * defined locally here.
 *
 * Mud is a DRILLING-ONLY input domain: POST/PUT return 422 for non-drilling
 * events (the panel disables the add/edit controls and surfaces the note). The
 * existing GET `/events/{id}/mud-checks` query on `pApi` remains the list; after
 * a write the panel calls its `refetch()` (that query carries no cache tag, so
 * we refetch it directly rather than relying on tag invalidation).
 */
import { pApi } from "@api/pApi";
import type { MudProductTranOut } from "@api/types";

// ── Local request/response types (mirror the Oxbo-P contract) ───────────────

/**
 * A mud transaction list row that also carries the entity key. The shared read
 * type `MudProductTranOut` (in the untouched contract file) doesn't declare
 * `entity_id`/`version`, but the live P route serves them, and the PUT is keyed
 * on `entity_id`. We narrow the list rows to this locally so the edit affordance
 * has the key it needs without touching the contract types.
 */
export interface MudTransactionListRow extends MudProductTranOut {
  entity_id: string;
  version?: number;
}

/** POST/PUT body for a mud product transaction (all fields optional; PUT keys
 * on entity_id). */
export interface MudTransactionWrite {
  mud_product_id?: string | null;
  quantity_used?: number | null;
  quantity_received?: number | null;
  quantity_adjusted?: number | null;
  quantity_returned?: number | null;
  product_daily_cost?: number | null;
  currency?: string | null;
  change_reason?: string | null;
}

/** POST/PUT response for a mud product transaction (entity-versioned). */
export interface MudProductTranWriteOut {
  id: string;
  entity_id: string;
  version: number;
  event_id: string;
  daily_report_id: string | null;
  report_date?: string | null;
  mud_product_id: string | null;
  well_uwi: string;
  quantity_used: number | null;
  quantity_received: number | null;
  quantity_adjusted: number | null;
  quantity_returned: number | null;
  product_daily_cost: number | null;
  currency: string | null;
}

/** POST/PUT body for a mud volume (pit/tank reading). */
export interface MudVolumeWrite {
  location_entry_key?: string | null;
  mud_vol?: number | null;
  mud_vol_desc?: string | null;
  sequence_no?: number | null;
  change_reason?: string | null;
}

/** POST/PUT response for a mud volume (entity-versioned). */
export interface MudVolumeOut {
  id: string;
  entity_id: string;
  version: number;
  event_id: string;
  daily_report_id: string | null;
  well_uwi: string;
  location_entry_key: string | null;
  mud_vol: number | null;
  mud_vol_desc: string | null;
  sequence_no: number | null;
}

/** A product line on the full-job roll-up. Mirrors the event-scoped
 * MudProductOut shape the aggregate reuses. */
export interface FullJobMudProduct {
  id: string;
  event_id: string;
  well_uwi: string;
  product_name: string | null;
  vendor_entry_key: string | null;
  unit_price: number | null;
  currency: string | null;
  unit_size: number | null;
  unit_measure_entry_key: string | null;
  date_first_used: string | null;
}

/** GET /wells/{well_uwi}/full-job/mud — the whole-well mud roll-up. */
export interface FullJobMudResponse {
  well_uwi: string;
  unit_profile_key: string | null;
  products: FullJobMudProduct[];
  transactions: MudProductTranWriteOut[];
  total_product_cost: number | null;
  /** Reporting currency for total_product_cost; "MIXED" when the well's mud
   * lines span more than one currency (total is then not currency-comparable). */
  currency: string | null;
}

// ── Injected endpoints ──────────────────────────────────────────────────────

export const mudApi = pApi.injectEndpoints({
  endpoints: (b) => ({
    // Transactions ── POST /reports/{report_id}/mud/transactions
    createMudTransaction: b.mutation<
      MudProductTranWriteOut,
      { reportId: string; body: MudTransactionWrite }
    >({
      query: ({ reportId, body }) => ({
        url: `/reports/${reportId}/mud/transactions`,
        method: "POST",
        body,
      }),
    }),
    // PUT /reports/{report_id}/mud/transactions/{entity_id}
    updateMudTransaction: b.mutation<
      MudProductTranWriteOut,
      { reportId: string; entityId: string; body: MudTransactionWrite }
    >({
      query: ({ reportId, entityId, body }) => ({
        url: `/reports/${reportId}/mud/transactions/${entityId}`,
        method: "PUT",
        body,
      }),
    }),

    // Volumes ── POST /reports/{report_id}/mud/volumes
    createMudVolume: b.mutation<MudVolumeOut, { reportId: string; body: MudVolumeWrite }>({
      query: ({ reportId, body }) => ({
        url: `/reports/${reportId}/mud/volumes`,
        method: "POST",
        body,
      }),
    }),
    // PUT /reports/{report_id}/mud/volumes/{entity_id}
    updateMudVolume: b.mutation<
      MudVolumeOut,
      { reportId: string; entityId: string; body: MudVolumeWrite }
    >({
      query: ({ reportId, entityId, body }) => ({
        url: `/reports/${reportId}/mud/volumes/${entityId}`,
        method: "PUT",
        body,
      }),
    }),

    // Full-job roll-up ── GET /wells/{well_uwi}/full-job/mud
    getFullJobMud: b.query<FullJobMudResponse, string>({
      query: (wellUwi) => `/wells/${encodeURIComponent(wellUwi)}/full-job/mud`,
    }),
  }),
});

export const {
  useCreateMudTransactionMutation,
  useUpdateMudTransactionMutation,
  useCreateMudVolumeMutation,
  useUpdateMudVolumeMutation,
  useGetFullJobMudQuery,
} = mudApi;
